import { CommonHelper } from "../../helper/common-helper";
import { Enums } from "../../helper/enums";
import { Model } from "@vuex-orm/core";
import { DateTime } from "luxon";
import { getBrowserIanaTimeZone as localTz } from "../../helper/browser-time-zone";

import { _clientEntity } from "../../entity-service/client-entity-service";
import {
  _contactFullName,
  _contactEmailAddress,
} from "../../entity-service/contact-entity-service";
import {
  getTicketPriorityById,
  getTicketStatusEntity,
  _statusChangedInfo,
  _statusChangedReason,
} from "../../entity-service/global-status-entity-service";
import {
  getTechnicianFirstName,
  getTechnicianLastName,
  _fullName,
} from "../../entity-service/technician-entity-service";
import {
  _contactFirstName,
  _contactLastName,
  _contactName,
  _isTicketNotViewed,
  shouldMarkTicketUnreadForClient,
  _statusColor,
  _ticketTitleWithPrefix,
  _ticketTitleWithPrefixWithHyphen,
  _truncatedTitle,
  _secondaryContactList,
  type TicketRecentCommentSnapshot,
  _secondaryTechnicianList,
  _watcherList,
} from "../../entity-service/ticket-entity-service";
import { LastModifiedValue } from "../ticket/last-modified-model";
import {
  ticketRecentMessageAuthorName,
  ticketRecentMessagePreview,
} from "../../helper/ticket-recent-comment-preview";
import { ticketActivityMsForClientRead } from "../../helper/ticket-client-read-state";
import PrototypeInstance from "../../helper/prototype-instance";

function filterPositiveIds(ids: number[]): number[] {
  return ids.filter((id) => id > 0);
}

export interface ticketEntity {
  clientId: number;
  contractId: number;
  createdByContactId: number;
  createdByTechnicianId: number;
  createdOn: string;
  isActive: boolean | null;
  isMerged: boolean | null;
  serviceProviderId: number;
  serviceProviderTicketId: string | null;
  updatedByContactId: number;
  updatedByTechnicianId: number;
  updatedOn: string;
  onSite: boolean | null;
  ticketId: string;
  ticketTitle: string | null;
  ticketStatusId: number | null;
  ticketPriorityId: number | null;
  ticketSeverityId: number | null;
  ticketSourceId: number | null;
  tick?: number;
  assignedToId: number;
  contactId: number;
  ticketNumber: number;
  ticketNumberWithPrefix: string | null;
  lastModifiedJson: string | null;
  ticketTypeId: number | null;
  updatedByTechnician: boolean | null;
  ticketStatus: string | null;
  billingLocation: number;
  state: string | null;
  mentionedTechnicians: string | null;
  lastActionId: number;
  secondaryTechnicians: string | null;
  secondaryContacts: string | null;
  mergedTicketIds: string | null;
  mergedWithTicketId: string | null;
  invoiceStatus: number;
  agreementId: number;
  statusChangedMessage: string;
  isAcknowledged: boolean;
  acknowledgedOn: string;
  subscribedGroupIds: number[];
  watchers: string | null;
  recentCommentId: string | null;
  recentComment: TicketRecentCommentSnapshot | null;
  workType: number | null;
  billingRole: number | null;
  billableStatus: number | null;
  banner: string | null;
  ticketLocation: number;
  updateTicketCount: boolean;
  isFilling: boolean;
  viewOriginalComment: boolean;
  showStatusUpdateMenu: boolean;
  statusUpdateMenuKey: number;
  showAssigneeUpdateMenu: boolean;
  assigneeUpdateMenuKey: number;
  isSelected: boolean;
  isHighlighted: boolean;
  ticketClosedOn: string;
  isTicketProduct: boolean;
  isTicketAsset: boolean;
  isTicketTag: boolean;
  isTicketTime: boolean;
  isThirdPartyConversation: boolean;
  delayedActionsExist: boolean;
  isUnread: boolean;
  /** Set by `cx/list`, `tick/cx/list` and `cx/detail` when the logged-in contact has an approval on
   * this ticket still waiting on their answer. Drives the list's "Waiting for approval" chip. */
  isApprovalPending: boolean;
}

export class ticketEntity extends Model {
  static entity = "ticketEntity";
  static primaryKey = "ticketId";

  static fields() {
    return {
      clientId: this.number(0),
      contractId: this.number(0),
      createdByContactId: this.number(0),
      createdByTechnicianId: this.number(0),
      createdOn: this.attr(""),
      isActive: this.boolean(null),
      isMerged: this.boolean(null),
      mergedlongoTicketId: this.number(0),
      serviceProviderId: this.number(0),
      serviceProviderTicketId: this.string(null),
      updatedByContactId: this.number(0),
      updatedByTechnicianId: this.number(0),
      updatedOn: this.attr(""),
      onSite: this.boolean(null),
      ticketId: this.string(null),
      ticketTitle: this.string(null),
      ticketStatusId: this.attr(null),
      ticketPriorityId: this.attr(null),
      ticketSeverityId: this.attr(null),
      ticketSourceId: this.attr(null),
      isSelected: this.boolean(null),
      isHighlighted: this.boolean(false),
      tick: this.number(0),
      assignedToId: this.number(0),
      contactId: this.number(0),
      ticketNumber: this.number(0),
      ticketNumberWithPrefix: this.attr(null),
      lastModifiedJson: this.string(null),
      ticketTypeId: this.attr(null),
      updatedByTechnician: this.attr(null),
      ticketStatus: this.string(null),
      isFilling: this.boolean(true),
      billingLocation: this.number(0),
      state: this.attr(null),
      mentionedTechnicians: this.attr(null),
      lastActionId: this.number(0),
      viewOriginalComment: this.boolean(false), //To be used in UI only
      showStatusUpdateMenu: this.boolean(false), //To be used in UI only
      statusUpdateMenuKey: this.number(0), //To be used in UI only
      showAssigneeUpdateMenu: this.boolean(false), //To be used in UI only
      assigneeUpdateMenuKey: this.number(0), //To be used in UI only
      secondaryTechnicians: this.attr(null),
      secondaryContacts: this.attr(null),
      mergedTicketIds: this.attr(null),
      mergedWithTicketId: this.attr(null),
      invoiceStatus: this.number(0),
      agreementId: this.number(0),
      statusChangedMessage: this.string(""),
      isAcknowledged: this.boolean(false),
      acknowledgedOn: this.attr(""),
      subscribedGroupIds: this.attr([]),
      watchers: this.attr(null),
      recentCommentId: this.attr(null),
      recentComment: this.attr(null),
      workType: this.attr(null),
      billingRole: this.attr(null),
      billableStatus: this.attr(null),
      banner: this.attr(null),
      updateTicketCount: this.boolean(true),
      ticketLocation: this.number(0),
      ticketClosedOn: this.attr(""),
      isTicketProduct: this.boolean(false),
      isTicketAsset: this.boolean(false),
      isTicketTag: this.boolean(false),
      isTicketTime: this.boolean(false),
      isThirdPartyConversation: this.boolean(false),
      delayedActionsExist: this.boolean(false),
      isUnread: this.boolean(false),
      isApprovalPending: this.boolean(false),
    };
  }

  static afterCreate(record: any) {
    let isTicketFilled: number =
      PrototypeInstance.globalProperties.$store.getters[
        "ticketDetailModule/getTicketFillingState"
      ];
    if (!isTicketFilled) {
      return;
    }
    const store = PrototypeInstance.globalProperties.$store;
    const selectedTicketId = store.getters[
      "ticketDetailModule/getTicketId"
    ] as string;
    const viewerContactId = store.getters[
      "ContactModule/getContactId"
    ] as number;
    record.isUnread = shouldMarkTicketUnreadForClient(
      Number(record.lastActionId ?? 0),
      String(record.ticketId ?? ""),
      selectedTicketId,
      record.lastModifiedJson,
      {
        viewerContactId,
        recentComment: record.recentComment ?? null,
        recentCommentId: record.recentCommentId ?? null,
        createdByTechnicianId: record.createdByTechnicianId,
        createdByContactId: record.createdByContactId,
        ticketContactId: record.contactId,
      },
      ticketActivityMsForClientRead(record),
    );
  }

  static beforeUpdate(_record: any) {}

  static afterUpdate(_record: any) {}

  static afterDelete(_record: any) {}

  static beforeSelect(records: any) {
    return records.filter((row: any) => row.ticketId != "0");
  }

  get clientEntity() {
    return _clientEntity(this.clientId);
  }

  get ticketStatusEntity(): any {
    return getTicketStatusEntity(this.ticketStatusId);
  }

  get ticketPriorityEntity() {
    return getTicketPriorityById(this.ticketPriorityId);
  }

  get statusSortOrder(): number {
    return CommonHelper.getTicketStatusSortOrder(this.ticketStatusId);
  }

  get ticketTitleWithPrefix() {
    return _ticketTitleWithPrefix(
      this.ticketNumberWithPrefix,
      this.ticketTitle,
    );
  }

  get ticketTitleWithPrefixWithHyphen() {
    return _ticketTitleWithPrefixWithHyphen(
      this.ticketNumberWithPrefix,
      this.ticketTitle,
    );
  }

  get truncatedTitle() {
    return _truncatedTitle(this.ticketTitle);
  }

  get statusColor() {
    return _statusColor(this.ticketStatusId);
  }

  get customCreatedOn() {
    return CommonHelper.getDateWithTodayYesterdayFormat(this.createdOn);
  }

  get customUpdatedOn() {
    return CommonHelper.getDateWithTodayYesterdayFormat(this.updatedOn);
  }

  get customTicketClosedOn() {
    return CommonHelper.getDateWithTodayYesterdayFormat(this.ticketClosedOn);
  }

  get isDateSameAsTodayOrYesterday() {
    return this.checkIsDateSameAsTodayOrYesterday(this.updatedOn);
  }

  get secondaryTechnicianList(): any[] {
    return _secondaryTechnicianList(this.secondaryTechnicians);
  }

  get watcherList(): any[] {
    return _watcherList(this.watchers);
  }

  get secondaryContactList(): any[] {
    return _secondaryContactList(this.secondaryContacts);
  }

  checkIsDateSameAsTodayOrYesterday(date: string): boolean {
    const tz = localTz();
    let currentDate = DateTime.fromISO(DateTime.utc().toString(), {
      zone: "utc",
    }).setZone(tz);
    let yesterdayDate = DateTime.fromISO(
      DateTime.utc().minus({ days: 1 }).toString(),
      { zone: "utc" },
    ).setZone(tz);

    let timeZomeConversionFormat = DateTime.fromISO(date, {
      zone: "utc",
    }).setZone(tz);
    return (
      timeZomeConversionFormat.hasSame(currentDate, "day") ||
      timeZomeConversionFormat.hasSame(yesterdayDate, "day")
    );
  }

  get isTicketNotViewed() {
    return _isTicketNotViewed(this.lastActionId);
  }

  get contactName() {
    return _contactName(this.createdByTechnicianId, this.createdByContactId);
  }

  /** Sidebar / list preview: author of the latest public message. */
  get recentMessageAuthorName(): string {
    return ticketRecentMessageAuthorName(this);
  }

  /** Sidebar / list preview: plain-text snippet of the latest message. */
  get recentMessagePreview(): string {
    return ticketRecentMessagePreview(this);
  }

  get contactFirstName() {
    return _contactFirstName(
      this.createdByTechnicianId,
      this.createdByContactId,
    );
  }

  get contactLastName() {
    return _contactLastName(
      this.createdByTechnicianId,
      this.createdByContactId,
    );
  }

  get lastModifiedEditedType() {
    let lastModifiedObject = JSON.parse(
      this.lastModifiedJson,
    ) as LastModifiedValue;
    if (lastModifiedObject) {
      return lastModifiedObject.editedType;
    } else {
      return 0;
    }
  }

  get showTooltipOnLastModifiedText(): boolean {
    return (
      (this.lastModifiedEditedType == 8 && this.hasStatusChangedMessage) ||
      this.lastModifiedEditedType == 30
    );
  }

  get lastModifiedObject() {
    return JSON.parse(this.lastModifiedJson) as LastModifiedValue;
  }

  get jsUpdatedOn() {
    if (this.updatedOn != null) {
      return DateTime.fromISO(this.updatedOn, {
        zone: "utc",
      }).toJSDate();
    }
    return null;
  }

  get jsCreatedOn() {
    if (this.createdOn != null) {
      return new Date((this as any).createdOn);
    }
    return null;
  }

  get hasStatusChangedMessage() {
    let self = this as any;
    return (
      self.ticketStatusId == 6 &&
      self.statusChangedMessage &&
      self.statusChangedMessage != "null"
    );
  }

  get statusChangedReason() {
    return _statusChangedReason(this.statusChangedMessage);
  }

  get statusChangedInfo() {
    return _statusChangedInfo(this.statusChangedMessage);
  }

  get getAllTicketTechnicians(): number[] {
    let ticketTechnicians: number[] = [];
    ticketTechnicians.push(this.assignedToId);
    ticketTechnicians.push(this.createdByTechnicianId);
    ticketTechnicians.push(this.updatedByTechnicianId);
    this.secondaryTechnicianList.forEach((value) => {
      ticketTechnicians.push(value);
    });
    return filterPositiveIds(ticketTechnicians);
  }

  get getAllAssignee(): number[] {
    let ticketTechnicians: number[] = [];
    ticketTechnicians.push(this.assignedToId);
    this.secondaryTechnicianList.forEach((value) => {
      ticketTechnicians.push(value);
    });
    this.watcherList.forEach((value) => {
      ticketTechnicians.push(value);
    });
    return filterPositiveIds(ticketTechnicians);
  }

  get getAllTicketContacts(): number[] {
    let ticketContacts: number[] = [];
    ticketContacts.push(this.contactId);
    ticketContacts.push(this.createdByContactId);
    ticketContacts.push(this.updatedByContactId);
    this.secondaryContactList.forEach((value) => {
      ticketContacts.push(value);
    });
    return filterPositiveIds(ticketContacts);
  }

  get getLastModifiedById(): number {
    let lastModifiedBy: number = 0;
    let lastModifiedObject = JSON.parse(
      this.lastModifiedJson,
    ) as LastModifiedValue;
    if (lastModifiedObject) {
      if (lastModifiedObject.version == 1) {
        lastModifiedBy =
          this.updatedByTechnicianId > 0
            ? this.updatedByTechnicianId
            : this.createdByTechnicianId;
      } else {
        lastModifiedBy = lastModifiedObject.by;
      }
    }
    return lastModifiedBy;
  }

  get getContactFullName(): any {
    return _contactFullName(this.clientId, this.contactId);
  }

  get getContactEmailAddress(): string {
    return _contactEmailAddress(this.contactId);
  }

  //Technician Used Properties after breaking relations between ticket and Technician
  get technicianFullName() {
    return _fullName(this.assignedToId);
  }

  get technicianFirstName() {
    return getTechnicianFirstName(this.assignedToId);
  }

  get technicianLastName() {
    return getTechnicianLastName(this.assignedToId);
  }
}
